import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse, PaginationAwareObject } from './index'
import type { IUser } from '../auth'
import { ClientModel } from '../clients'

export interface ISoftwareVersion {
    id: number
    version: string
    model: ClientModel
    base64Bin: string
    firmwareUrl?: string
    uploadedBy: IUser
    changeLog?: string
    createdAt?: Date
    updatedAt?: Date
}

export async function getSoftwareVersionPagination(page: number, model?: ClientModel): Promise<ApiResponse<PaginationAwareObject<ISoftwareVersion>>> {
    const modelParam = model !== undefined ? `&model=${model}` : ''
    const response = await fetch(`${HOST_URL}software-versions/pagination?page=${page}&per_page=2${modelParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<PaginationAwareObject<ISoftwareVersion>>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function isSoftwareOutdated(
    version: ISoftwareVersion['version'],
    model: ClientModel,
): Promise<ApiResponse<{ outdated: boolean; softwareVersion?: ISoftwareVersion }>> {
    const response = await fetch(`${HOST_URL}software-versions/check/${version}?model=${model}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<any>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getAllSoftwareVersions(model?: ClientModel): Promise<ApiResponse<ISoftwareVersion[]>> {
    const modelParam = model !== undefined ? `?model=${model}` : ''
    const response = await fetch(`${HOST_URL}software-versions${modelParam}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<ISoftwareVersion[]>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getSoftwareVersion(id: ISoftwareVersion['id']): Promise<ApiResponse<ISoftwareVersion>> {
    const response = await fetch(`${HOST_URL}software-versions/${id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<ISoftwareVersion>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function createSoftwareVersion(softwareVersionData: ISoftwareVersion): Promise<ApiResponse<ISoftwareVersion>> {
    const response = await fetch(`${HOST_URL}software-versions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(softwareVersionData),
    })
    const result = (await response.json()) as ApiResponse<ISoftwareVersion>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function updateSoftwareVersion(
    id: ISoftwareVersion['id'],
    softwareVersionData: ISoftwareVersion,
): Promise<ApiResponse<ISoftwareVersion>> {
    const response = await fetch(`${HOST_URL}software-versions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(softwareVersionData),
    })
    const result = (await response.json()) as ApiResponse<ISoftwareVersion>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function deleteSoftwareVersion(id: ISoftwareVersion['id']): Promise<ApiResponse<ISoftwareVersion>> {
    const response = await fetch(`${HOST_URL}software-versions/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<ISoftwareVersion>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}
